import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { finalize, firstValueFrom, take, takeUntil } from 'rxjs';

import {
  CardSetTransformationService,
  CoreService,
  DataService,
  DialogResult,
  DialogService,
  FIELD_TYPE,
  FormNavigationService,
  SaveDataRequest,
  SpinnerService
} from 'dspx-core';

import {
  CUSTOM_FIELD_TYPE,
  minWaitForDialogToCloseMs,
  NotAssessedQuestionKeySuffix,
  NOTIFICATION_CODES,
  POP_UP_CODES,
  READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS,
  SAVE_RESULT_STATUS,
  SERVER_ERROR_POP_UP_CODES
} from '../../constants/app-data.constants';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { VERSIONS } from '../../constants/templates.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';

import { GuidParser, Guid } from '../../helpers/guid-parser';
import { FileUploadHelper } from '../../helpers/file-upload-helper';
import { DateTimeHelper } from '../../helpers/date-time-helper';

import { ServiceWorkerService } from '../../services/service-worker.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';
import { ResponseParserService } from '../../services/response-parser.service';

import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { PrimeNGConfig } from 'primeng/api';

@Component({
  selector: 'app-inspection',
  templateUrl: './inspection.component.html',
  styleUrl: './inspection.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class InspectionComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetFieldTypeReplacements
    .map(tokenReplacement => ({ ...tokenReplacement })); // Making a deep copy, because we are modifying it later
  id = '';
  formServiceEndpoint: string;
  templateId: number = 0;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  managerComments: string = '';
  departmentComments: string = '';
  previousInspectionData: any = null;
  lastInspectionDate: string = null;

  private pin: number;
  private address: string = '';
  private suburb: string = '';
  private postCode: string = '';
  private canAddVendorManagerFeedback = false;
  private canAddDepartmentFeedback = false;
  private canViewFeedback = false;
  private canAcceptInspection = false;
  private canRejectInspection = false;
  private canSubmitInspection = false;
  private canQAReviewInspection = false;
  private isRevalidatingRepairFields = false;

  private readonly partialInspectionPopup = {
    code: 'partialInspection',
    title: 'Partial Inspection',
    text: '<p>Do you want to submit a partial inspection?</p>',
    type: 'confirm',
    onCloseFocusElementId: '',
    okButtonText: 'OK',
    cancelButtonText: 'Cancel'
  };

  constructor(
    protected router: Router,
    protected serviceWorkerService: ServiceWorkerService,
    protected appDataService: AppDataService,
    protected spinnerService: SpinnerService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected formNavigationService: FormNavigationService,
    private route: ActivatedRoute,
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService,
    private errorService: ErrorService,
    private dataService: DataService,
    private primengConfig: PrimeNGConfig,) {

    super(
      router,
      serviceWorkerService,
      appDataService,
      spinnerService,
      htmlHelperService,
      dialogService,
      responseParserService,
      formNavigationService);
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      const templateId = parseInt(paramMap.get('templateId') ?? '0');
      const versionNo = parseInt(paramMap.get('versionNo') ?? '0');
      if (id && templateId) { // templateId is necessary because it is used by the fetch interception in the ServiceWorker
        this.id = id;
        this.templateId = templateId;
        this.versionNo = versionNo;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getInspectionConfigAndResponse;
        this.editing = true;
      }
      else {
        this.resetDirtyFlagAfterLoading();
      }
    });
    
    this.overrideHtmlTitleSetByDspxFrameworkOnFormNavigation();

    this.waitForServiceWorker().then(waitResult => {
      // Using CardSetTransformationService.cardSetFieldTypeReplacements,
      // except that instead of replacing 'page' with 'cardsetPage', we replace it with 'cardSetPageWithCollapsibleSections' (and do a similar replacement for sections).
      this.fieldTypeReplacements.filter(tokenReplacement => tokenReplacement.old === FIELD_TYPE.page)[0].new = CUSTOM_FIELD_TYPE.cardSetPageWithCollapsibleSections;
      this.fieldTypeReplacements.filter(tokenReplacement => tokenReplacement.old === FIELD_TYPE.section)[0].new = CUSTOM_FIELD_TYPE.cardSectionCollapsible;
      this.fieldTypeReplacements.filter(tokenReplacement => tokenReplacement.old === FIELD_TYPE.sectionRepeating)[0].new = CUSTOM_FIELD_TYPE.cardSectionRepeatingCollapsible;
    });

    this.coreService.conditionFunctions['alwaysTrue'] = () => true;

    this.coreService.applicationFunctions['canAccessFeedback'] = () => this.canAddVendorManagerFeedback || this.canAddDepartmentFeedback || this.canViewFeedback;
    this.coreService.applicationFunctions['showFeedbackForm'] = () => this.openFeedbackDialog(null);

    this.coreService.applicationFunctions['canAcceptInspection'] = () => this.canAcceptInspection;
    this.coreService.applicationFunctions['acceptInspection'] = () => this.acceptInspection(null);

    this.coreService.applicationFunctions['canRejectInspection'] = () => this.canRejectInspection;
    this.coreService.applicationFunctions['rejectInspection'] = () => this.rejectInspection(null);

    this.coreService.applicationFunctions['canSubmitInspection'] = () => this.canSubmitInspection;
    this.coreService.applicationFunctions['submitInspection'] = () => this.submitInspection(null);

    this.coreService.applicationFunctions['validateRepairCostSection'] = (parameters: [any, string, string, string, string, string]) => this.validateRepairCostSection(...parameters);

    this.coreService.repeatingSectionFunctions['getCustomSectionItemHeaderHtml'] = (config: FormlyFieldConfig, sectionIndex: number) =>
      this.getCustomSectionItemHeaderHtml(config, sectionIndex);

    this.coreService.applicationFunctions['fileUploadOnComplete'] = (file: File) => this.fileUploadOnComplete(file);

    //Fixed overlay positioning by forcing all PrimeNG overlays to append to body, as dspx-select cannot be changed.
    this.primengConfig.overlayOptions = {
      appendTo: 'body'
    };
  }

  ngOnDestroy(): void {
    this.completeDestroy();
  }

  formLoaded(event: any): void {
    this.templateId = event.template;
    this.versionNo = event.versionNumber;
    this.isFormEditable = !!event?.isFormEditable;

    const customLoadData = event?.customLoadData ?? {};
    this.pin = customLoadData?.pin;
    this.address = customLoadData?.address ?? '';
    this.suburb = customLoadData?.suburb ?? '';
    this.postCode = customLoadData?.postCode ?? '';
    this.canAddVendorManagerFeedback = !!customLoadData.canAddVendorManagerFeedback;
    this.canAddDepartmentFeedback = !!customLoadData.canAddDepartmentFeedback;
    this.canViewFeedback = !!customLoadData.canViewFeedback;
    this.canAcceptInspection = !!customLoadData.canAcceptInspection;
    this.canRejectInspection = !!customLoadData.canRejectInspection;
    this.canSubmitInspection = !!customLoadData.canSubmitInspection;
    this.canQAReviewInspection = !!customLoadData.canQAReviewInspection;
    this.managerComments = customLoadData.managerComments ?? '';
    this.departmentComments = customLoadData.departmentComments ?? '';
    this.previousInspectionData = customLoadData.latestCompletedInspectionResponse;
    this.lastInspectionDate = customLoadData.lastInspectionDate;

    if (this.previousInspectionData) {
      //update with previous data if exists
      this.injectDataRecursively(this.baseForm.fields, this.previousInspectionData);
    }    

    this.setPreviousModel({ isAsync: true });

    const formIdentifier: Guid = GuidParser.fromStringOrNumber(event.id);
    const fileUploadQuestionCodes = FileUploadHelper.getFileUploadQuestionPathsAndConfigs(this.formConfigurationParserService, this.baseForm)
      .map(question => <string>question.questionConfig.key);

    fileUploadQuestionCodes.forEach(fileUploadQuestionCode => this.coreService.fileUploadFormIdentifiers[fileUploadQuestionCode] = formIdentifier);

    this.fileUploadQuestions = FileUploadHelper.getFileUploadQuestionGetModelFunctions(this.formConfigurationParserService, this.responseParserService, this.baseForm);

    
    const pagePaths = this.formConfigurationParserService.getPagePaths(this.baseForm.fields);
    this.ensurePageActionButtonsAreEnabledOnFormLoad(pagePaths, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.inspection);    
  }

  save(event: any): void {
    const saveDataRequest: SaveDataRequest = this.getSaveDataRequest();
    this.saveData(saveDataRequest);
  }

  cancel(event: any): void {
    this.navigateBackToDashboard();
  }

  openFeedbackDialog(event: any): void {
    if (this.canAddVendorManagerFeedback) {
      this.activeDialog = POP_UP_CODES.inspection.addManagerFeedback;
      this.dialogService.showPopup(this.activeDialog,
        {
          comments: this.managerComments
        },
        {
          // The manager feedback popup displays the department comments if they exist
          managerFeedbackMessage: getDepartmentFeedbackMessage(this.departmentComments)
        });
    }
    else if (this.canAddDepartmentFeedback) {
      this.activeDialog = POP_UP_CODES.inspection.addDepartmentFeedback;
      this.dialogService.showPopup(this.activeDialog,
        {
          comments: this.departmentComments
        },
        {
          // The department feedback popup displays the manager comments if they exist
          departmentFeedbackMessage: getManagerFeedbackMessage(this.managerComments)
        });
    }
    else if (this.canViewFeedback) {
      this.activeDialog = POP_UP_CODES.inspection.viewInspectionFeedback;

      const noFeedbackMessage = !this.managerComments && !this.departmentComments ? '<p>No Feedback recorded</p>' : '';
      const managerFeedbackMessage = getManagerFeedbackMessage(this.managerComments);
      const departmentFeedbackMessage = this.departmentComments ? `<p>Feedback from Department: ${this.departmentComments}</p>` : '';
      const feedbackMessage = noFeedbackMessage + managerFeedbackMessage + departmentFeedbackMessage;

      this.dialogService.showPopup(this.activeDialog,
        {},
        {
          feedbackMessage: feedbackMessage
        });
    }

    function getManagerFeedbackMessage(managerComments: string): string {
      return managerComments ? `<p>Feedback from Manager: ${managerComments}</p>` : '';
    }

    function getDepartmentFeedbackMessage(departmentComments: string): string {
      return departmentComments ? `<p>Feedback from Department: ${departmentComments}</p>` : '';
    }
  }

  acceptInspection(event: any): void {
    const hasPartialInspection = this.hasNotAssessed(this.baseForm.model);

    if (this.canQAReviewInspection) {
      this.postInspectionAction(
        API_ENDPOINTS.inspection.acceptInspectionByQA,
        {
          inspectionId: this.id,
          expectedLastUpdateTime: this.baseForm.lastUpdatedTime
        }
      );
    }
    else {
      const submitData = () => {
        this.postInspectionAction(
          API_ENDPOINTS.inspection.acceptInspection,
          {
            inspectionId: this.id,
            expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
            response: JSON.stringify(this.baseForm.model),
            versionNumber: this.versionNo,
            isCompleted: !hasPartialInspection
          }
        );
      };

      if (hasPartialInspection) {
        this.showPartialInspectionPopup(submitData);
      } else {
        submitData();
      }

    }
  }

  rejectInspection(event: any): void {
    if(this.canQAReviewInspection) {
      this.postInspectionAction(
        API_ENDPOINTS.inspection.rejectInspectionByQA,
        { 
          inspectionId: this.id,
          expectedLastUpdateTime: this.baseForm.lastUpdatedTime
        }
      );
    }
    else {
      this.showReassignInspectorPopupToReject();
    }
  }

  showReassignInspectorPopupToReject() {
    this.activeDialog = POP_UP_CODES.inspection.reAssignInspector;
    this.dialogService.showPopup(this.activeDialog);
  }

  submitInspection(event: any): void {
    const hasPartialInspection = this.hasNotAssessed(this.baseForm.model);

    const submitData = () => {
      const apiEndpoint = API_ENDPOINTS.inspectionList.syncCompletedInspections;
      const request = {
        inspections: [
          {
            inspectionId: this.id,
            template: this.templateId,
            templateId: this.templateId,
            pin: this.pin,
            canAccessProperty: true,
            responseData: JSON.stringify(this.baseForm.model),
            inspectionCompletedDate: DateTimeHelper.now().toISO(),
            address: this.address,
            suburb: this.suburb,
            postCode: this.postCode,
            isCompleted: !hasPartialInspection
          }
        ],
        shouldUpdateStatus: true
      };
      this.spinnerService.showSpinner();
      this.appDataService.post(apiEndpoint, request)
        .pipe(
          take(1),
          takeUntil(this.destroy$),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner()))
        .subscribe(data => {
          firstValueFrom(this.dialogService.showSimplePopup('Inspection details have been submitted successfully.', 'Submit Inspection', 'Close'))
            .then(() => this.navigateBackToDashboard(true));
        });
    };

    const proceedAfterValidation = () => {
      if (hasPartialInspection) {
       this.showPartialInspectionPopup(submitData);
      } else {
        submitData();
      }

    }

    if (!this.baseForm.form.valid) {
      this.baseForm.markAllFieldsAsTouched();

      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showPopup(POP_UP_CODES.common.validationError);
      } else {
        proceedAfterValidation();
      }
    } else {
      proceedAfterValidation();
    }
  }

  fileUploadOnComplete(file: File): string {
    const date = new Date(file.lastModified);
    return date.toLocaleString('en-AU', { hour12: true });
  }

  getCustomSectionItemHeaderHtml(config: FormlyFieldConfig, sectionIndex: number): string {
    const sectionLabel = config?.parent?.props?.label ?? 'Item';
    const itemIndex = sectionIndex > 0 ? sectionIndex : '';
    return `<h5>${sectionLabel} ${itemIndex}</h5>`;
  }

  validateRepairCostSection(
    repairField: any,
    repairFieldShortQuestionKey: string,
    repairYearQuestionCode: string,
    repairAreaLengthCountQuestionCode: string,
    repairDescriptionQuestionCode: string,
    repairCostQuestionCode: string
  ): boolean {
    const repairYearKey = 'repairyear';
    const repairAreaLengthCountKey = 'repairarea/length/count';
    const repairDescriptionKey = 'repairdescription';
    const repairCostKey = 'repaircost';

    const getFieldValue = (fields: any[], questionCode: string) => 
      fields?.find(f => f.key === questionCode)?.formControl?.value;
    
    const siblingFields = repairField?.parent?.fieldGroup;
    const repairYear = getFieldValue(siblingFields, repairYearQuestionCode);
    const repairAreaLengthCount = getFieldValue(siblingFields, repairAreaLengthCountQuestionCode);
    const repairCost = getFieldValue(siblingFields, repairCostQuestionCode);

    const repairFieldValue = repairField?.formControl?.value;

    switch (repairFieldShortQuestionKey) {
      case repairYearKey:
        if ((repairAreaLengthCount || repairCost) && !repairFieldValue) {
          return false;
        }
        break;
      case repairAreaLengthCountKey:
        if (repairYear && !repairFieldValue) {
          return false;
        }
        break;
      case repairDescriptionKey:
        if (repairYear && !repairFieldValue) {
          return false;
        }
        break;
      case repairCostKey:
        if (repairYear && !repairFieldValue) {
          return false;
        }
        break;
      default:
        console.error('Unknown repair field short question key:', repairFieldShortQuestionKey);
        return false;
    }

    this.validateSiblingRepairFields(repairYearQuestionCode, repairAreaLengthCountQuestionCode, repairCostQuestionCode, repairDescriptionQuestionCode, repairField);

    return true;
  }

  private validateSiblingRepairFields(
    repairYearQuestionCode: string,
    repairAreaLengthCountQuestionCode: string,
    repairCostQuestionCode: string,
    repairDescriptionQuestionCode: string,
    repairField: any
  ) {
    // avoid infinite loop when we validate each sibling field using this boolean flag.
    // this validation routine will be called again for sibling fields when we mark them as touched
    // and update their validity, but we don't want to re-enter this routine at that point to avoid infinite loop.
    if (!this.isRevalidatingRepairFields) {
      this.isRevalidatingRepairFields = true;

      const siblingQuestionCodes = [
        repairYearQuestionCode,
        repairAreaLengthCountQuestionCode,
        repairCostQuestionCode,
        repairDescriptionQuestionCode
      ];

      const currentKey = repairField?.key;
      const siblingFields = repairField?.parent?.fieldGroup;

      siblingFields
        ?.filter(field => field.key !== currentKey && siblingQuestionCodes.includes(field.key))
        ?.forEach(field => {
          field.formControl?.markAsTouched();
          field.formControl?.updateValueAndValidity();
        });

      this.isRevalidatingRepairFields = false;
    }
  }

  dialogButton_saveSuccessful_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_addManagerFeedback_Clicked(event: any): void {
    this.postInspectionAction(
      API_ENDPOINTS.inspection.saveManagerComments,
      { 
        inspectionId: this.id,
        managerComments: event?.comments,
        expectedLastUpdateTime: this.baseForm.lastUpdatedTime
      },
      null,
      NOTIFICATION_CODES.inspection.saveSuccessNotification,
      (data) => {
        this.baseForm.lastUpdatedTime = data?.allocatedLastUpdatedTime ?? this.baseForm.lastUpdatedTime;
        this.managerComments = data?.managerComments ?? this.managerComments;
      }
    );
  }

  dialogButton_addDepartmentFeedback_Clicked(event: any): void {
    this.postInspectionAction(
      API_ENDPOINTS.inspection.saveDepartmentComments,
      {
        inspectionId: this.id,
        departmentComments: event?.comments,
        expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
        versionNumber: this.versionNo
      },
      null,
      NOTIFICATION_CODES.inspection.saveSuccessNotification,
      (data) => {
        this.baseForm.lastUpdatedTime = data?.allocatedLastUpdatedTime ?? this.baseForm.lastUpdatedTime;
        this.departmentComments = data?.departmentComments ?? this.departmentComments;
      }
    );
  }

  dialogButton_reAssignInspector_Clicked(event: any): void {
    const hasPartialInspection = this.hasNotAssessed(this.baseForm.model);

    this.postInspectionAction(
      API_ENDPOINTS.inspection.rejectInspection,
      {
        inspectionId: this.id,
        expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
        versionNumber: this.versionNo,
        response: JSON.stringify(this.baseForm.model),
        isCompleted: !hasPartialInspection,
        assignedUserId: event.selectInspector
      }
    );
  }

  dialogButton_successfulInspectionReject_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  dialogButton_successfulInspectionApprove_Clicked(event: any): void {
    this.navigateBackToDashboard(true);
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.inspection.list], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest): void {
    this.spinnerService.showSpinner();
    this.dataService.saveData(API_ENDPOINTS.inspection.save, request)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        if (data?.status === SAVE_RESULT_STATUS.common.success) {
          if (SERVER_ERROR_POP_UP_CODES.saveInspection.includes(data.message)) {
            this.activeDialog = data.message;
            this.dialogService.showPopup(this.activeDialog);
          }
          else {
            this.id = data.id;
            this.baseForm.lastUpdatedTime = data.allocatedLastUpdatedTime;
            this.setPreviousModel({ isAsync: true });
            this.showNotification(NOTIFICATION_CODES.common.saveSuccessful);
          }
        }
      });
  }

  private getSaveDataRequest(): SaveDataRequest {
    return {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: JSON.stringify(this.baseForm.model),
      expectedLastUpdateTime: this.baseForm.lastUpdatedTime,
      isSubmit: false
    };
  }

  private postInspectionAction(
    apiEndPoint: string,
    payload: any,
    resultPopupCode?: string,
    resultNotificationCode?: string,
    successCallback?: (data) => void
  ): void {
    this.spinnerService.showSpinner();
    this.appDataService.post(apiEndPoint, payload)
      .pipe(take(1),
        takeUntil(this.destroy$),
        this.errorService.errorHandler(),
        finalize(() => this.spinnerService.hideSpinner())
      )
      .subscribe(data => {
        const responsePopupCode = data?.message;
        
        if(responsePopupCode) {
          this.openDialogForResult(data, responsePopupCode);
        }
        else if (resultPopupCode) {
          this.openDialogForResult(data, resultPopupCode);
        }
        else if (resultNotificationCode) {
          this.showNotification(resultNotificationCode);
        }

        if(successCallback && !responsePopupCode) {
          successCallback(data);
        }
      });
  }

  private openDialogForResult(model: any, popupCode: string): void {
    this.activeDialog = popupCode;
    let messagePlaceholders: any;

    switch(popupCode) {
      case POP_UP_CODES.inspection.savedElsewhere:
      case POP_UP_CODES.inspection.saveInspectionError:
      case POP_UP_CODES.inspection.successfulInspectionReject:
      case POP_UP_CODES.inspection.successfulInspectionApprove:
        messagePlaceholders = {};
        break;
      case POP_UP_CODES.inspectionList.rejectInspectionResult:      
        messagePlaceholders = {
          rejectInspectionResultMessage: model?.message
        };
        break;
      case POP_UP_CODES.inspectionList.rejectInspectionByQAResult:
        messagePlaceholders = {
          rejectInspectionByQAResultMessage: model?.message
        };
        break;
      default:
        messagePlaceholders = {};
        console.error('Unknown popup code:', popupCode);
    }
    this.dialogService.showPopup(this.activeDialog, {}, messagePlaceholders);
  }

  private injectDataRecursively(fields: FormlyFieldConfig[], responseData: any): void {
    fields?.forEach(field => {

      if (field.type === CUSTOM_FIELD_TYPE.cardSectionCollapsible ||
        field.type === CUSTOM_FIELD_TYPE.cardSectionRepeatingCollapsible) {

        field.props ??= {};

        field.props.previousInspectionData = responseData;
        field.props.lastInspectionDate = this.lastInspectionDate;
      }

      if (field.fieldGroup && field.fieldGroup.length > 0) {
        this.injectDataRecursively(field.fieldGroup, responseData);
      }
    });
  }

  private hasNotAssessed(data: any): boolean {
    if (data == null) {
      return false;
    }

    if (Array.isArray(data)) {
      return data.some(item => this.hasNotAssessed(item));
    }

    if (typeof data === 'object') {
      return Object.entries(data).some(([key, value]) =>
        (key.endsWith(NotAssessedQuestionKeySuffix) && value === true) ||
        this.hasNotAssessed(value)
      );
    }

    return false;
  }

  private showPartialInspectionPopup(submitCallback: () => void): void {
    this.dialogService.setPopups([this.partialInspectionPopup]);

    this.dialogService
      .showConfirmPopup(this.partialInspectionPopup.code)
      .pipe(take(1)) 
      .subscribe(result => {
        if (result === DialogResult.ACCEPTED) {
          setTimeout(() => submitCallback(), minWaitForDialogToCloseMs);
        }
      });
  }

}
